/**
 * ReferNewPatientCTA Component
 *
 * Call-to-action component for creating new patient referrals.
 * Features gradient background, hover effects, loading states,
 * and accessibility support for the provider dashboard.
 *
 * @module components/provider-dashboard/ReferNewPatientCTA
 */

import React from 'react';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { useTheme } from '@/hooks/useTheme';
import type { ReferNewPatientCTAProps } from '@/types/dashboard';
import { cn } from '@/utils/cn';

/**
 * Call-to-action component for new patient referrals
 */
export const ReferNewPatientCTA: React.FC<ReferNewPatientCTAProps> = ({
  onReferClick,
  disabled = false,
  loading = false,
  className,
}) => {
  const { isDark } = useTheme();
  return (
    <Card
      className={cn(
        'relative overflow-hidden border-0 p-0',
        className
      )}
    >
      {/* Gradient background */}
      <div className={`bg-gradient-to-r ${isDark ? 'from-primary-700 via-secondary-700 to-primary-900' : 'from-primary-600 via-secondary-600 to-primary-800'}`}>
        <div className="px-6 py-8 sm:px-8 sm:py-10">
          <div className="flex flex-col items-center text-center sm:flex-row sm:text-left">
            {/* Content section */}
            <div className="flex-1 mb-6 sm:mb-0 sm:mr-6">
              <h2 className="text-2xl font-bold text-white sm:text-3xl">
                Ready to refer a new patient?
              </h2>
              <p className={`mt-2 sm:text-lg ${isDark ? 'text-primary-200' : 'text-primary-100'}`}>
                Connect your patients with qualified Accredited Practicing Dietitians in our network
              </p>
            </div>

            {/* CTA button section */}
            <div className="flex-shrink-0">
              <Button
                onClick={onReferClick}
                disabled={disabled || loading}
                className={cn(
                  'bg-white border-0 shadow-lg hover:shadow-xl transition-all duration-200',
                  'px-3 py-3 text-lg font-semibold',
                  'focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-blue-600',
                  isDark ? 'text-primary-700 hover:bg-blue-50 focus:bg-blue-50' : 'text-primary-600 hover:bg-blue-50 focus:bg-blue-50',
                  {
                    'opacity-50 cursor-not-allowed': disabled,
                    'cursor-wait': loading,
                  }
                )}
                aria-label="Start new patient referral process"
                data-testid="create-referral-button"
              >
                {loading ? (
                  <div className="flex items-center">
                    <div className="animate-spin mr-2 h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full" />
                    Processing...
                  </div>
                ) : (
                  <div className="flex items-center">
                    <span className="mr-2" aria-hidden="true">➕</span>
                    Refer Patient
                  </div>
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Decorative elements */}
        <div className="absolute top-0 right-0 -mt-4 -mr-4 opacity-20">
          <div className="h-24 w-24 rounded-full bg-white/10" />
        </div>
        <div className="absolute bottom-0 left-0 -mb-6 -ml-6 opacity-10">
          <div className="h-32 w-32 rounded-full bg-white/10" />
        </div>
      </div>
    </Card>
  );
};

export default ReferNewPatientCTA;
